import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Home, Layers, Trophy, Gift, Flame, User, Shield, Building2, LogOut,
  Menu, X, Calendar, Users, FileText, BarChart3, DollarSign, Sparkles,
} from "lucide-react";
import { useState } from "react";
import MentorWidget from "./MentorWidget";

const USER_NAV = [
  { to: "/dashboard", label: "Dashboard", Icon: Home, testid: "nav-dashboard" },
  { to: "/categories", label: "Categories", Icon: Layers, testid: "nav-categories" },
  { to: "/challenges", label: "Challenges", Icon: Flame, testid: "nav-challenges" },
  { to: "/events", label: "Events", Icon: Calendar, testid: "nav-events" },
  { to: "/leaderboards", label: "Leaderboards", Icon: Trophy, testid: "nav-leaderboards" },
  { to: "/perks", label: "Perks", Icon: Gift, testid: "nav-perks" },
  { to: "/profile", label: "Profile", Icon: User, testid: "nav-profile" },
];

const ADMIN_NAV = [
  { to: "/admin", label: "Admin home", Icon: Shield, testid: "nav-admin", end: true },
  { to: "/admin?tab=users", label: "Users", Icon: Users, testid: "nav-admin-users" },
  { to: "/admin?tab=tasks", label: "Content", Icon: FileText, testid: "nav-admin-content" },
  { to: "/admin?tab=events", label: "Events", Icon: Calendar, testid: "nav-admin-events" },
  { to: "/admin?tab=challenges", label: "Challenges", Icon: Flame, testid: "nav-admin-challenges" },
  { to: "/admin?tab=sponsorships", label: "Sponsorships", Icon: DollarSign, testid: "nav-admin-sponsorships" },
  { to: "/admin?tab=orgs", label: "Organisations", Icon: Building2, testid: "nav-admin-orgs" },
  { to: "/admin?tab=impact", label: "Impact", Icon: BarChart3, testid: "nav-admin-impact" },
];

const ORG_NAV = [
  { to: "/org", label: "Cohort dashboard", Icon: Building2, testid: "nav-org" },
];

const SPONSOR_NAV = [
  { to: "/partner", label: "Partner home", Icon: Sparkles, testid: "nav-partner" },
];

function navForRole(role) {
  if (role === "admin") return ADMIN_NAV;
  if (role === "org_manager") return ORG_NAV;
  if (role === "sponsor") return SPONSOR_NAV;
  return USER_NAV;
}

function brandLineForRole(role) {
  if (role === "admin") return { label: "Admin Console", color: "text-neoncyan" };
  if (role === "org_manager") return { label: "Organisation Portal", color: "text-neongreen" };
  if (role === "sponsor") return { label: "Partner Portal", color: "text-neonyellow" };
  return null;
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role || "user";
  const NAV = navForRole(role);
  const brandLine = brandLineForRole(role);
  const isUser = role === "user";

  return (
    <div className="min-h-screen bg-aurora text-white">
      {/* Top bar (mobile) */}
      <div className="md:hidden flex items-center justify-between px-5 py-4 border-b border-white/5 bg-ink/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="flex flex-col">
          <div className="font-display text-xl font-black tracking-tight">
          <span className="text-gradient-pink">S</span><span className="text-neoncyan">0</span><span className="text-gradient-pink">rted</span>
          </div>
          {brandLine && <div className={`text-[10px] uppercase tracking-widest font-bold ${brandLine.color}`}>{brandLine.label}</div>}
        </div>
        <button onClick={() => setMobileOpen(o => !o)} className="p-2 rounded-full bg-white/10" data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? "block" : "hidden"} md:block w-full md:w-64 md:min-h-screen border-r border-white/5 bg-ink/60 backdrop-blur-xl md:sticky md:top-0 md:h-screen z-30`}>
          <div className="hidden md:flex flex-col px-6 py-6">
            <div className="font-display text-2xl font-black tracking-tight">
              <span className="text-gradient-pink">S</span><span className="text-neoncyan">0</span><span className="text-gradient-pink">rted</span>
            </div>
            {brandLine && (
              <div className={`text-[10px] uppercase tracking-widest font-bold mt-1 ${brandLine.color}`} data-testid="role-banner">
                {brandLine.label}
              </div>
            )}
          </div>

          <nav className="px-3 space-y-1">
            {NAV.map(({ to, label, Icon, testid, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-2xl transition ${
                    isActive
                      ? "bg-neonpink/15 text-white border border-neonpink/40 shadow-glow"
                      : "text-white/70 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon size={18}/>
                <span className="font-semibold">{label}</span>
              </NavLink>
            ))}

            {/* Admin can also browse user-facing pages for QA */}
            {role === "admin" && (
              <div className="pt-3 mt-3 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-widest font-bold text-white/35 px-4 mb-1">View as user</div>
                <NavLink to="/dashboard" onClick={() => setMobileOpen(false)} className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2 rounded-xl text-sm transition ${
                    isActive ? "bg-white/8 text-white" : "text-white/55 hover:bg-white/5 hover:text-white"
                  }`} data-testid="nav-view-as-user">
                  <Home size={14}/><span>User dashboard</span>
                </NavLink>
              </div>
            )}
          </nav>

          {user && (
            <div className="hidden md:block absolute bottom-6 left-3 right-3">
              <div className="glass-card p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  role === "admin" ? "bg-gradient-to-br from-neoncyan to-neongreen text-ink" :
                  role === "sponsor" ? "bg-gradient-to-br from-neonyellow to-neonpink text-ink" :
                  role === "org_manager" ? "bg-gradient-to-br from-neongreen to-neoncyan text-ink" :
                  "bg-gradient-to-br from-neonpink to-neoncyan"
                }`}>
                  {(user.name || "U")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate text-sm" data-testid="sidebar-user-name">{user.name}</div>
                  <div className="text-xs text-white/60 truncate">
                    {isUser && <>Lvl {user.level} · {user.xp} XP</>}
                    {role === "admin" && "Administrator"}
                    {role === "org_manager" && "Org Manager"}
                    {role === "sponsor" && (user.company || "Partner")}
                  </div>
                </div>
                <button onClick={async () => { await logout(); navigate("/"); }}
                  className="p-2 rounded-full hover:bg-white/10" data-testid="logout-button" title="Logout">
                  <LogOut size={16}/>
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 px-5 md:px-10 lg:px-12 py-6 md:py-10 pb-32" key={location.pathname}>
          <Outlet/>
        </main>
      </div>

      {/* AI Mentor: only for end users */}
      {isUser && <MentorWidget/>}
    </div>
  );
}
